/**
 * The permission matrix, mirrored for the browser (BP-01 R8, ARCHITECTURE §3 Auth).
 *
 * **The api is the enforcer.** Every route is gated server-side by
 * `api/app/core/security/permissions.py`; this copy exists so a screen can hide a
 * control the caller may not use and so BP-01 §6's route gates ("`settings.manage`",
 * "owner/manager", "any staff") can be decided before a request is made. It is a
 * convenience, never a control.
 *
 * The vocabulary is **closed**: `PERMISSIONS` is exactly the 33 strings
 * ARCHITECTURE §3 Auth lists, in document order, and the assignments are R8's
 * seed, verbatim. `permissions.test.ts` pins both against the api module.
 */

/** ARCHITECTURE §3 Auth's six roles (`user_role.role`). */
export const ROLES = [
  'owner',
  'manager',
  'frontdesk',
  'technician',
  'accountant',
  'client',
] as const;

export type Role = (typeof ROLES)[number];

/** The canonical vocabulary — ARCHITECTURE §3 Auth, in document order. Exactly 33. */
export const PERMISSIONS = [
  'client.read',
  'client.read_full',
  'client.write',
  'client.merge',
  'dsr.manage',
  'breach.manage',
  'catalogue.read',
  'catalogue.write',
  'inventory.read',
  'inventory.write',
  'purchase.manage',
  'scheduling.read',
  'scheduling.write',
  'session.run',
  'session.manage',
  'billing.read',
  'billing.write',
  'billing.issue',
  'billing.void',
  'billing.close_day',
  'payments.manage',
  'commission.read',
  'commission.manage',
  'printing.read',
  'printing.manage',
  'accounting.read',
  'accounting.manage',
  'reports.read',
  'reports.export',
  'settings.manage',
  'users.manage',
  'licence.manage',
  'backup.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** R8: "`owner` = all 33". */
const OWNER: readonly Permission[] = PERMISSIONS;

/** R8: "`manager` = owner minus `licence.manage`, `backup.manage`, `breach.manage`, `billing.void`". */
const MANAGER: readonly Permission[] = PERMISSIONS.filter(
  (name) =>
    name !== 'licence.manage' &&
    name !== 'backup.manage' &&
    name !== 'breach.manage' &&
    name !== 'billing.void',
);

/** R8, verbatim (15 strings). */
const FRONTDESK: readonly Permission[] = [
  'client.read',
  'client.read_full',
  'client.write',
  'catalogue.read',
  'inventory.read',
  'inventory.write',
  'scheduling.read',
  'scheduling.write',
  'session.run',
  'billing.read',
  'billing.write',
  'billing.issue',
  'printing.read',
  'printing.manage',
  'reports.read',
];

/** R8, verbatim. */
const TECHNICIAN: readonly Permission[] = [
  'client.read',
  'catalogue.read',
  'inventory.read',
  'scheduling.read',
  'session.run',
  'commission.read',
];

/** R8, verbatim (11 strings). */
const ACCOUNTANT: readonly Permission[] = [
  'client.read',
  'billing.read',
  'payments.manage',
  'commission.read',
  'commission.manage',
  'accounting.read',
  'accounting.manage',
  'reports.read',
  'reports.export',
  'inventory.read',
  'purchase.manage',
];

/** The matrix. R8: "`client` = ∅ (client routes gate on role + ownership)". */
export const PERMS: Readonly<Record<Role, readonly Permission[]>> = {
  owner: OWNER,
  manager: MANAGER,
  frontdesk: FRONTDESK,
  technician: TECHNICIAN,
  accountant: ACCOUNTANT,
  client: [],
};

/** True when `value` is one of the six roles. */
export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/**
 * The union of the permissions granted by `roles`.
 *
 * A string that is not one of the six roles grants nothing: `user_role.role` is
 * CHECK-constrained server-side, so this can only be a stale token or a bug, and
 * granting nothing is the safe reading of either.
 */
export function permissionsFor(roles: readonly string[]): ReadonlySet<Permission> {
  const granted = new Set<Permission>();
  for (const role of roles) {
    if (isRole(role)) {
      for (const permission of PERMS[role]) {
        granted.add(permission);
      }
    }
  }
  return granted;
}

/** True when `roles` grant `permission`. */
export function hasPermission(roles: readonly string[], permission: Permission): boolean {
  return permissionsFor(roles).has(permission);
}

/** True when `roles` include any of `wanted`. */
export function hasAnyRole(roles: readonly string[], wanted: readonly Role[]): boolean {
  return roles.some((role) => (wanted as readonly string[]).includes(role));
}

/**
 * True for a staff caller — any role other than `client`.
 *
 * Mirrors `requires_second_factor()` in `api/app/core/security/totp.py`, which is
 * what the api's "any non-client" rows gate on, and is BP-01 §6's gate for
 * `/settings/security`.
 */
export function isStaff(roles: readonly string[]): boolean {
  return roles.some((role) => isRole(role) && role !== 'client');
}
